import React, { useState, useEffect, useRef, useCallback } from "react";
import NovoEventoService from "../../services/Eventos/NovoEventoService";
import { Button, Input, Toast, Modal, Spinner } from "../ui";
import { UserPlus, Trash2, RefreshCw, Users, UserMinus, Info, Check } from "lucide-react";

function GerenciarCoOrganizadores({ corridaId, corridaNome, isProprietario = true, onClose }) {
    const [coOrganizadores, setCoOrganizadores] = useState([]);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [adicionando, setAdicionando] = useState(false);
    const [dialogExcluir, setDialogExcluir] = useState(false);
    const [usuarioParaExcluir, setUsuarioParaExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);

    const toast = useRef(null);
    const eventoService = useRef(new NovoEventoService());
    const usuarioLogadoId = Number(sessionStorage.getItem("brv-user-id"));

    const carregarCoOrganizadores = useCallback(async () => {
        if (!corridaId) return;
        setLoading(true);
        try {
            const response = await eventoService.current.getCoOrganizadores(corridaId);
            if (response.ok) {
                const data = await response.json();
                setCoOrganizadores(Array.isArray(data) ? data : []);
            } else {
                let errorMsg = "Erro ao carregar equipe.";
                try {
                    const errData = await response.json();
                    if (errData.erro) errorMsg = errData.erro;
                    else if (errData.detail) errorMsg = errData.detail;
                } catch (_) {}
                toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: errorMsg,
                    life: 3500
                });
            }
        } catch (error) {
            console.error("Erro ao carregar co-organizadores:", error);
            toast.current?.show({
                severity: "error",
                summary: "Erro de Conexão",
                detail: "Não foi possível carregar a lista de co-organizadores.",
                life: 3500
            });
        } finally {
            setLoading(false);
        }
    }, [corridaId]);

    useEffect(() => {
        carregarCoOrganizadores();
    }, [carregarCoOrganizadores]);

    const handleAdicionar = async (e) => {
        e?.preventDefault();
        const emailTrim = email.trim();
        if (!emailTrim) {
            toast.current?.show({
                severity: "warn",
                summary: "Atenção",
                detail: "Por favor, digite o e-mail do co-organizador.",
                life: 3000
            });
            return;
        }

        setAdicionando(true);
        try {
            const response = await eventoService.current.adicionarCoOrganizador(corridaId, emailTrim);
            const data = await response.json().catch(() => null);

            if (response.status === 201) {
                toast.current?.show({
                    severity: "success",
                    summary: "Sucesso!",
                    detail: data?.detalhe || "Co-organizador adicionado com sucesso!",
                    life: 3500
                });
                setEmail("");
                if (data?.co_organizador) {
                    setCoOrganizadores((prev) => [...prev, data.co_organizador]);
                } else {
                    carregarCoOrganizadores();
                }
            } else if (response.status === 404) {
                toast.current?.show({
                    severity: "error",
                    summary: "Usuário não encontrado",
                    detail: data?.erro || `Nenhum usuário cadastrado com o e-mail "${emailTrim}". Solicite que o usuário crie uma conta na plataforma primeiro.`,
                    life: 5000
                });
            } else if (response.status === 400) {
                toast.current?.show({
                    severity: "warn",
                    summary: "Não permitido",
                    detail: data?.erro || data?.detail || "Usuário já é proprietário ou co-organizador desta corrida.",
                    life: 4000
                });
            } else if (response.status === 403) {
                toast.current?.show({
                    severity: "error",
                    summary: "Acesso Negado",
                    detail: data?.erro || "Apenas o organizador proprietário pode adicionar co-organizadores.",
                    life: 4000
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Erro ao adicionar",
                    detail: data?.erro || data?.detail || "Ocorreu um erro ao adicionar o co-organizador.",
                    life: 4000
                });
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            toast.current?.show({
                severity: "error",
                summary: "Erro de Comunicação",
                detail: "Falha na conexão com o servidor ao adicionar co-organizador.",
                life: 4000
            });
        } finally {
            setAdicionando(false);
        }
    };

    const handleConfirmarExclusao = async () => {
        if (!usuarioParaExcluir) return;
        setExcluindo(true);
        try {
            const response = await eventoService.current.removerCoOrganizador(corridaId, usuarioParaExcluir.id);
            const data = await response.json().catch(() => null);

            if (response.ok || response.status === 200) {
                toast.current?.show({
                    severity: "success",
                    summary: "Acesso Revogado",
                    detail: data?.detalhe || "Co-organizador removido com sucesso.",
                    life: 3000
                });
                setCoOrganizadores((prev) => prev.filter((u) => u.id !== usuarioParaExcluir.id));
                setDialogExcluir(false);
                setUsuarioParaExcluir(null);
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Erro ao remover",
                    detail: data?.erro || "Não foi possível remover o co-organizador.",
                    life: 4000
                });
            }
        } catch (error) {
            console.error("Erro ao remover:", error);
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Falha na conexão ao remover o co-organizador.",
                life: 4000
            });
        } finally {
            setExcluindo(false);
        }
    };

    return (
        <div className="flex flex-col gap-4 text-white">
            <Toast ref={toast} />

            {/* Cabeçalho do evento */}
            <div className="p-4 rounded-xl bg-slate-900 border-l-4 border-urb-primary border-t border-r border-b border-urb-border flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="font-bold text-lg text-white m-0">
                        {corridaNome || "Evento"}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1 m-0">
                        Gerencie quem tem acesso de edição, acompanhamento de inscrições e visualização do financeiro desta corrida.
                    </p>
                </div>
                {!isProprietario && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/40">
                        Você é Co-organizador
                    </span>
                )}
            </div>

            {/* Formulário de convite */}
            {isProprietario && (
                <form onSubmit={handleAdicionar} className="flex flex-col gap-2 p-4 rounded-xl bg-urb-card border border-urb-border">
                    <label htmlFor="coorg_email" className="font-semibold text-xs text-slate-300">
                        Convidar novo organizador pelo e-mail
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2 w-full">
                        <div className="flex-1">
                            <Input
                                id="coorg_email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Digite o e-mail do co-organizador (ex: colega@email.com)"
                                disabled={adicionando}
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={adicionando || !email.trim()}
                            className="w-full sm:w-auto px-5 py-2.5 font-bold flex items-center justify-center gap-2 shadow-neon whitespace-nowrap"
                        >
                            <UserPlus className="w-4 h-4" />
                            {adicionando ? "Adicionando..." : "Adicionar"}
                        </Button>
                    </div>
                    <small className="text-slate-400 text-xs flex items-center gap-1.5 mt-1">
                        <Info className="w-3.5 h-3.5 text-urb-primary shrink-0" />
                        O usuário deve possuir uma conta cadastrada na Urbrace com este mesmo e-mail.
                    </small>
                </form>
            )}

            {/* Lista de co-organizadores */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm text-white">
                        Co-organizadores com acesso ({coOrganizadores.length})
                    </span>
                    <button
                        type="button"
                        onClick={carregarCoOrganizadores}
                        disabled={loading}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                        title="Atualizar lista"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <div className="bg-urb-card border border-urb-border rounded-xl overflow-hidden shadow-md">
                    {loading && coOrganizadores.length === 0 ? (
                        <div className="flex items-center justify-center py-10">
                            <Spinner size="md" />
                        </div>
                    ) : coOrganizadores.length === 0 ? (
                        <div className="text-center py-8 px-4 text-slate-400 text-sm">
                            <Users className="w-8 h-8 text-slate-500 mx-auto mb-2 opacity-60" />
                            Nenhum co-organizador adicionado até o momento.
                        </div>
                    ) : (
                        <div className="divide-y divide-urb-border">
                            {coOrganizadores.map((row) => {
                                const nomeCompleto = `${row.first_name || ""} ${row.last_name || ""}`.trim();
                                const podeRemover = isProprietario || row.id === usuarioLogadoId;
                                return (
                                    <div key={row.id} className="p-3.5 flex items-center justify-between gap-3 hover:bg-slate-900/40 transition-colors">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-white text-sm">
                                                    {nomeCompleto || row.username || "Sem nome"}
                                                </span>
                                                {row.id === usuarioLogadoId && (
                                                    <span className="text-[11px] font-bold text-urb-primary">
                                                        (Você)
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs text-slate-400 font-mono">
                                                {row.email}
                                            </span>
                                        </div>

                                        <div>
                                            <button
                                                type="button"
                                                disabled={!podeRemover}
                                                onClick={() => {
                                                    setUsuarioParaExcluir(row);
                                                    setDialogExcluir(true);
                                                }}
                                                title={
                                                    podeRemover
                                                        ? row.id === usuarioLogadoId
                                                            ? "Sair desta equipe"
                                                            : "Revogar acesso deste organizador"
                                                        : "Apenas o proprietário pode remover"
                                                }
                                                className={`p-2 rounded-lg transition-colors ${
                                                    podeRemover
                                                        ? "text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                                        : "text-slate-600 cursor-not-allowed"
                                                }`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Confirmação de Exclusão */}
            <Modal
                isOpen={dialogExcluir}
                onClose={() => !excluindo && setDialogExcluir(false)}
                title="Remover Acesso"
                maxWidth="max-w-md"
            >
                <div className="flex flex-col items-center text-center gap-3 py-2">
                    <div className="w-14 h-14 rounded-full bg-red-500/15 text-red-500 flex items-center justify-center">
                        <UserMinus className="w-7 h-7" />
                    </div>

                    <h4 className="font-bold text-lg text-white m-0">
                        {usuarioParaExcluir?.id === usuarioLogadoId
                            ? "Deseja sair da equipe desta corrida?"
                            : "Remover este co-organizador?"}
                    </h4>

                    {usuarioParaExcluir && (
                        <div className="p-3 rounded-lg bg-slate-900 border-l-4 border-red-500 border border-urb-border w-full text-left">
                            <div className="font-bold text-sm text-white">
                                {`${usuarioParaExcluir.first_name || ""} ${usuarioParaExcluir.last_name || ""}`.trim() || usuarioParaExcluir.username}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                                {usuarioParaExcluir.email}
                            </div>
                        </div>
                    )}

                    <p className="text-xs text-slate-400 m-0">
                        {usuarioParaExcluir?.id === usuarioLogadoId
                            ? "Você perderá o acesso para visualizar e acompanhar os dados e o financeiro deste evento."
                            : "Este usuário não poderá mais visualizar ou gerenciar os dados desta corrida."}
                    </p>

                    <div className="flex justify-end gap-2 w-full mt-4 pt-3 border-t border-urb-border">
                        <Button
                            variant="secondary"
                            onClick={() => setDialogExcluir(false)}
                            disabled={excluindo}
                            className="text-xs px-4"
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmarExclusao}
                            disabled={excluindo}
                            className="text-xs px-4 font-bold flex items-center gap-1.5"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            {excluindo ? "Removendo..." : "Remover"}
                        </Button>
                    </div>
                </div>
            </Modal>

            <div className="flex justify-end mt-2 pt-3 border-t border-urb-border">
                <Button
                    variant="secondary"
                    onClick={onClose}
                    className="flex items-center gap-2 text-xs font-semibold px-5 py-2"
                >
                    <Check className="w-4 h-4 text-urb-primary" />
                    Fechar
                </Button>
            </div>
        </div>
    );
}

export default GerenciarCoOrganizadores;
